import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import WhatsAppConnectPanel from "@/components/integrations/WhatsAppConnectPanel";
import {
  Building2,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Upload,
  Users,
  Mail,
  Zap,
  CreditCard,
  UserCircle,
  Lock,
  ShieldCheck,
} from "lucide-react";

type Step = "about" | "company" | "whatsapp" | "domain" | "plan" | "payment" | "done";

const STEPS_FREE: Step[] = ["about", "company", "whatsapp", "domain", "plan", "done"];
const STEPS_PAID: Step[] = ["about", "company", "whatsapp", "domain", "plan", "payment", "done"];

const TEAM_SIZE_OPTIONS = [
  { value: "1", label: "Só eu" },
  { value: "2-5", label: "2 a 5 pessoas" },
  { value: "6-20", label: "6 a 20 pessoas" },
  { value: "21-50", label: "21 a 50 pessoas" },
  { value: "51+", label: "Mais de 50 pessoas" },
];

const JOB_TITLE_OPTIONS = [
  "CEO / Fundador(a) / Sócio(a)",
  "Diretor(a)",
  "Gerente de Marketing",
  "Gerente Comercial / Vendas",
  "Coordenador(a) de Marketing",
  "Coordenador(a) Comercial / Vendas",
  "Analista de Marketing",
  "Analista Comercial / Vendas",
  "Especialista em Tráfego / Performance",
  "Consultor(a) / Autônomo(a)",
  "Outro",
];

const BRAZILIAN_STATES = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
  "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

function formatCNPJ(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function formatCPF(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function formatNumber(n: number): string {
  if (n >= 999999) return "Ilimitado";
  return n.toLocaleString("pt-BR");
}

export default function Onboarding() {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>("about");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Plan selection
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  // About (profile)
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [hasMarketingTeam, setHasMarketingTeam] = useState<boolean | null>(null);

  // Company data
  const [docType, setDocType] = useState<"cnpj" | "cpf">("cnpj");
  const [docNumber, setDocNumber] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [useSameEmail, setUseSameEmail] = useState(true);
  const [billingEmail, setBillingEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#22c55e");

  // WhatsApp: conectado pelo Cadastro Incorporado dentro do passo (o painel
  // grava direto no workspace); aqui só guardamos se deu certo, para o texto.
  const [whatsappConectado, setWhatsappConectado] = useState(false);

  // Email domain
  const [onboardingDomain, setOnboardingDomain] = useState("");
  const [onboardingDomainAdding, setOnboardingDomainAdding] = useState(false);
  const [onboardingDomainResult, setOnboardingDomainResult] = useState<any>(null);
  const createDomainMut = trpc.emailDomains.create.useMutation();

  const { data: tenant } = trpc.tenant.current.useQuery();
  const { data: plans = [], isLoading: plansLoading } = trpc.tenant.listPlans.useQuery();
  const completeOnboarding = trpc.tenant.completeOnboarding.useMutation();
  const checkout = trpc.payment.checkout.useMutation();

  useEffect(() => {
    if (tenant?.name && !companyName) setCompanyName(tenant.name);
  }, [tenant?.name]);

  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const isPaidPlan = selectedPlan && selectedPlan.key !== "free";
  const steps = isPaidPlan ? STEPS_PAID : STEPS_FREE;
  const currentIndex = steps.indexOf(step);

  const userEmail = user?.email || "";
  const effectiveBillingEmail = useSameEmail ? userEmail : billingEmail;

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      setError("Formato inválido. Use PNG, JPEG ou WebP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Arquivo muito grande. Máximo 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  /**
   * Salva os dados da empresa.
   *
   * `aplicarPlano` é falso no caminho pago: ali o plano escolhido só passa a
   * valer quando o Stripe confirmar o pagamento (webhook) — gravá-lo antes
   * entregaria o plano pago a quem desistisse na tela de cartão. Sem planId,
   * o servidor grava o gratuito (ele conhece o catálogo completo; esta lista
   * aqui só traz os planos ativos da vitrine).
   */
  const completeOnboardingData = async (aplicarPlano = true) => {
    if (!selectedPlanId) return;
    await completeOnboarding.mutateAsync({
      planId: aplicarPlano ? selectedPlanId : undefined,
      companyName: companyName || undefined,
      jobTitle: jobTitle || undefined,
      teamSize: teamSize || undefined,
      hasMarketingTeam: hasMarketingTeam ?? undefined,
      cnpj: docNumber || undefined,
      phone: companyPhone || undefined,
      billingEmail: effectiveBillingEmail || undefined,
      address: address || undefined,
      city: city || undefined,
      state: state || undefined,
      zipCode: zipCode || undefined,
      logoUrl: logoUrl || undefined,
      primaryColor,
    });
  };

  const handlePlanSubmit = async () => {
    if (!selectedPlanId) return;
    setLoading(true);
    setError("");
    try {
      if (isPaidPlan) {
        // Go to payment step
        setStep("payment");
      } else {
        // Free plan: complete onboarding directly
        await completeOnboardingData();
        setStep("done");
      }
    } catch (e: any) {
      setError(e.message || "Erro ao salvar configurações.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Leva para o Checkout do Stripe. O cartão é digitado lá, nunca aqui — por
   * isso não há campo de cartão nesta tela.
   *
   * Os dados do onboarding são salvos ANTES de sair da página: se a pessoa
   * abandonar o pagamento, o que ela já preencheu (empresa, WhatsApp) não se
   * perde. O PLANO, esse sim, só passa a valer quando o Stripe confirmar o
   * pagamento pelo webhook.
   */
  const handlePaymentSubmit = async () => {
    if (!selectedPlanId) return;
    setLoading(true);
    setError("");
    try {
      await completeOnboardingData(false);
      const { url } = await checkout.mutateAsync({ planId: selectedPlanId });
      window.location.href = url;
    } catch (e: any) {
      setError(e.message || "Não foi possível abrir a tela de pagamento.");
      setLoading(false);
    }
  };

  const handleFinish = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Progress bar */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step === s
                    ? "bg-primary text-primary-foreground"
                    : currentIndex > i
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-500"
                }`}
              >
                {currentIndex > i ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`w-8 sm:w-12 h-0.5 transition-colors ${
                    currentIndex > i ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
          {/* ── Step 1: About ── */}
          {step === "about" && (
            <>
              <div className="text-center mb-6">
                <UserCircle className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Nos conte mais sobre a {companyName || "sua empresa"}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Essas informações nos ajudam a personalizar sua experiência.
                </p>
              </div>
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label>Qual o seu cargo?</Label>
                  <select
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">Selecione seu cargo</option>
                    {JOB_TITLE_OPTIONS.map((title) => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Qual o tamanho da equipe?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {TEAM_SIZE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setTeamSize(opt.value)}
                        className={`px-3 py-2.5 rounded-lg border text-sm transition-all ${
                          teamSize === opt.value
                            ? "border-primary bg-primary/5 text-primary font-medium ring-2 ring-primary/20"
                            : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Tem uma equipe de marketing?</Label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setHasMarketingTeam(true)}
                      className={`flex-1 px-4 py-2.5 rounded-lg border text-sm transition-all ${
                        hasMarketingTeam === true
                          ? "border-primary bg-primary/5 text-primary font-medium ring-2 ring-primary/20"
                          : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                      }`}
                    >
                      Sim
                    </button>
                    <button
                      type="button"
                      onClick={() => setHasMarketingTeam(false)}
                      className={`flex-1 px-4 py-2.5 rounded-lg border text-sm transition-all ${
                        hasMarketingTeam === false
                          ? "border-primary bg-primary/5 text-primary font-medium ring-2 ring-primary/20"
                          : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                      }`}
                    >
                      Não
                    </button>
                  </div>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <Button
                  className="w-full"
                  onClick={() => {
                    if (!jobTitle) { setError("Selecione seu cargo."); return; }
                    if (!teamSize) { setError("Selecione o tamanho da equipe."); return; }
                    if (hasMarketingTeam === null) { setError("Informe se você tem uma equipe de marketing."); return; }
                    setError("");
                    setStep("company");
                  }}
                >
                  Continuar <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* ── Step 2: Company Data ── */}
          {step === "company" && (
            <>
              <div className="text-center mb-6">
                <Building2 className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Dados da {companyName || "Empresa"}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Informações cadastrais e identidade visual.</p>
              </div>
              <div className="space-y-4">
                {/* Logo + Color */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Logo (opcional)</Label>
                    <div className="flex items-center gap-3">
                      {logoUrl && (
                        <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-lg object-cover border" />
                      )}
                      <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-sm">
                        <Upload className="w-4 h-4" />
                        {logoUrl ? "Trocar" : "Escolher"}
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                      </label>
                      {logoUrl && (
                        <button className="text-xs text-gray-400 hover:text-red-500" onClick={() => setLogoUrl("")}>
                          Remover
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Cor Primária</Label>
                    <div className="flex gap-2">
                      <Input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-12 h-10 p-1" />
                      <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="flex-1" />
                    </div>
                  </div>
                </div>

                {/* Document (CNPJ/CPF) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{docType === "cnpj" ? "CNPJ" : "CPF"}</Label>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => { setDocType(docType === "cnpj" ? "cpf" : "cnpj"); setDocNumber(""); }}
                    >
                      {docType === "cnpj" ? "Não tenho CNPJ, usar CPF" : "Usar CNPJ"}
                    </button>
                  </div>
                  <Input
                    placeholder={docType === "cnpj" ? "00.000.000/0000-00" : "000.000.000-00"}
                    value={docNumber}
                    onChange={(e) => setDocNumber(docType === "cnpj" ? formatCNPJ(e.target.value) : formatCPF(e.target.value))}
                    maxLength={docType === "cnpj" ? 18 : 14}
                  />
                </div>

                {/* Billing Email */}
                <div className="space-y-2">
                  <Label>E-mail de Faturamento</Label>
                  {useSameEmail ? (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md border text-sm text-gray-700 dark:text-gray-300">
                        {userEmail}
                      </div>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                        onClick={() => setUseSameEmail(false)}
                      >
                        Usar outro e-mail
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Input
                        type="email"
                        placeholder="faturamento@empresa.com"
                        value={billingEmail}
                        onChange={(e) => setBillingEmail(e.target.value)}
                        className="flex-1"
                      />
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                        onClick={() => { setUseSameEmail(true); setBillingEmail(""); }}
                      >
                        Usar e-mail do login
                      </button>
                    </div>
                  )}
                </div>

                {/* CEP + Address */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <Input placeholder="00000-000" value={zipCode} onChange={(e) => setZipCode(e.target.value)} maxLength={10} />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Endereço</Label>
                    <Input placeholder="Rua, número, complemento" value={address} onChange={(e) => setAddress(e.target.value)} />
                  </div>
                </div>

                {/* City + State + Phone */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-2 col-span-1">
                    <Label>Cidade</Label>
                    <Input placeholder="São Paulo" value={city} onChange={(e) => setCity(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>UF</Label>
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="">--</option>
                      {BRAZILIAN_STATES.map((uf) => (
                        <option key={uf} value={uf}>{uf}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2 col-span-2 sm:col-span-2">
                    <Label>Telefone</Label>
                    <Input placeholder="(00) 00000-0000" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} />
                  </div>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep("about")}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                  </Button>
                  <Button className="flex-1" onClick={() => { setError(""); setStep("whatsapp"); }}>
                    Continuar <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* ── Step 3: WhatsApp Config ── */}
          {step === "whatsapp" && (
            <>
              <div className="text-center mb-6">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-green-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Conectar WhatsApp</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {whatsappConectado
                    ? "Número conectado. Você já pode seguir."
                    : "Entre com o Facebook e escolha o número. Leva um minuto."}
                </p>
              </div>
              <div className="space-y-4">
                <WhatsAppConnectPanel compact onConnected={() => setWhatsappConectado(true)} />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep("company")}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                  </Button>
                  <Button className="flex-1" onClick={() => { setError(""); setStep("domain"); }}>
                    Continuar <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                {!whatsappConectado && (
                  <button
                    className="w-full text-sm text-gray-400 hover:text-gray-600"
                    onClick={() => setStep("domain")}
                  >
                    Conectar depois, em Configurações → Integrações
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── Step: Email Domain ── */}
          {step === "domain" && (
            <>
              <div className="text-center mb-6">
                <Mail className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Domínio de E-mail</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Configure seu domínio para enviar e-mails com o endereço da sua empresa.
                </p>
              </div>
              <div className="space-y-4">
                {!onboardingDomainResult ? (
                  <>
                    <div className="space-y-2">
                      <Label>Domínio</Label>
                      <Input
                        value={onboardingDomain}
                        onChange={(e) => setOnboardingDomain(e.target.value)}
                        placeholder="seudominio.com"
                      />
                      <p className="text-xs text-muted-foreground">
                        Informe o domínio do e-mail que será usado como remetente (ex: empresa.com.br).
                      </p>
                    </div>
                    <Button
                      className="w-full"
                      disabled={onboardingDomainAdding || !onboardingDomain.trim()}
                      onClick={async () => {
                        setOnboardingDomainAdding(true);
                        setError("");
                        try {
                          const result = await createDomainMut.mutateAsync({ domain: onboardingDomain.trim().toLowerCase() });
                          setOnboardingDomainResult(result);
                        } catch (e: any) {
                          setError(e.message || "Erro ao cadastrar domínio");
                        } finally {
                          setOnboardingDomainAdding(false);
                        }
                      }}
                    >
                      {onboardingDomainAdding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Cadastrar Domínio
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 text-green-800 dark:text-green-300 text-sm">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      <span>Domínio <strong>{onboardingDomainResult.domain}</strong> cadastrado!</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-medium">
                      Configure estes registros CNAME no seu provedor de DNS:
                    </p>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-muted-foreground bg-muted/30">
                            <th className="p-2">Tipo</th>
                            <th className="p-2">Host</th>
                            <th className="p-2">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {onboardingDomainResult.dnsRecords && Object.entries(onboardingDomainResult.dnsRecords as Record<string, any>).map(([key, record]: [string, any]) => (
                            <tr key={key} className="border-t">
                              <td className="p-2 font-mono">CNAME</td>
                              <td className="p-2 font-mono break-all">{record.host}</td>
                              <td className="p-2 font-mono break-all">{record.data}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      A propagação DNS pode levar até 48h. Você pode verificar o domínio depois em Configurações &gt; Integrações.
                    </p>
                  </>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep("whatsapp")}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                  </Button>
                  <Button className="flex-1" onClick={() => { setError(""); setStep("plan"); }}>
                    Continuar <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <button
                  className="w-full text-sm text-gray-400 hover:text-gray-600"
                  onClick={() => setStep("plan")}
                >
                  Configurar depois
                </button>
              </div>
            </>
          )}

          {/* ── Step 4: Plan Selection ── */}
          {step === "plan" && (
            <>
              <div className="text-center mb-6">
                <CreditCard className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Escolha seu Plano</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Selecione o plano ideal para a {companyName || "sua empresa"}.
                </p>
              </div>

              {plansLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {plans.map((plan) => {
                    const isEnterprise = plan.key === "enterprise";
                    const isSelected = selectedPlanId === plan.id;
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => !isEnterprise && setSelectedPlanId(plan.id)}
                        className={`relative text-left rounded-xl border-2 p-5 transition-all ${
                          isSelected
                            ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                            : isEnterprise
                              ? "border-gray-200 dark:border-gray-600 opacity-75 cursor-default"
                              : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 cursor-pointer"
                        }`}
                      >
                        {plan.key === "pro" && (
                          <Badge className="absolute -top-2.5 right-4 text-xs">Popular</Badge>
                        )}
                        <div className="mb-3">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{plan.name}</h3>
                          <p className="text-primary font-bold text-xl mt-1">
                            {isEnterprise ? "Sob consulta" : plan.price}
                          </p>
                        </div>
                        <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-400">
                          <li className="flex items-center gap-2">
                            <Users className="w-3.5 h-3.5 flex-shrink-0" />
                            {formatNumber(plan.maxContacts)} contatos
                          </li>
                          <li className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                            {formatNumber(plan.maxMessagesPerMonth)} msgs/mês
                          </li>
                          <li className="flex items-center gap-2">
                            <Users className="w-3.5 h-3.5 flex-shrink-0" />
                            {formatNumber(plan.maxUsers)} usuários
                          </li>
                          <li className="flex items-center gap-2">
                            <Zap className="w-3.5 h-3.5 flex-shrink-0" />
                            {formatNumber(plan.maxFlows)} fluxos
                          </li>
                        </ul>
                        {isEnterprise && (
                          <p className="mt-3 text-xs text-gray-400">
                            Entre em contato para um plano personalizado.
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep("whatsapp")}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                </Button>
                <Button
                  className="flex-1"
                  disabled={!selectedPlanId || loading}
                  onClick={handlePlanSubmit}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {isPaidPlan ? "Ir para Pagamento" : "Finalizar"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* ── Step 5: Payment (only for paid plans) ── */}
          {step === "payment" && selectedPlan && (
            <>
              <div className="text-center mb-6">
                <Lock className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Pagamento</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Plano {selectedPlan.name} - <span className="font-semibold text-primary">{selectedPlan.price}</span>
                </p>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border bg-gray-50 dark:bg-gray-800/50 p-4 text-sm text-gray-600 dark:text-gray-300">
                  <p className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                      O pagamento é feito na página segura do Stripe. Seus dados de cartão não passam pelo LeadConversa.
                      Ao concluir, você volta direto para o painel.
                    </span>
                  </p>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep("plan")} disabled={loading}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                  </Button>
                  <Button className="flex-1" disabled={loading} onClick={handlePaymentSubmit}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                    Ir para o pagamento
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-2">
                  <Lock className="w-3 h-3" />
                  Pagamento seguro processado pelo Stripe
                </div>
              </div>
            </>
          )}

          {/* ── Final Step: Done ── */}
          {step === "done" && (
            <div className="text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Tudo Pronto!</h2>
              <p className="text-sm text-gray-500 mb-6">
                Sua conta foi configurada com sucesso. Você pode ajustar tudo em Configurações a qualquer momento.
              </p>
              <Button className="w-full" size="lg" onClick={handleFinish}>
                Ir para o Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
